import type { CandidateStatusParam } from '@/hooks/useRouterQuery';
import type { SvrEdgeNormal } from '@poseidon/types/svr/svrGraph.type';

/**
 * Static topology of the candidate pipeline — the journey a person takes from
 * being *added* to a campaign template until they are *enrolled* (a live
 * cadence is created), plus every dead-end. This mirrors the state machine that
 * actually drives the rows in `cadence_template_candidate`:
 *   - the status enum + partitions in
 *     `packages/server/src/api/services/cadenceTemplateCandidate.service.ts`
 *   - the transitions in `packages/server/src/apiWorker/candidateResearch.service.ts`
 *     and `worker.service.ts` (`runFilterAndPromote`, personalization, promote)
 *
 * The graph is FIXED (the pipeline is the same for every template); the
 * superadmin view overlays this template's live per-status counts and dims the
 * branches this particular template doesn't use (no filter rules / no
 * personalized steps). See `CandidateFlowGraph.tsx`.
 *
 * Layout is three columns on a shared row grid: a "blocked / auto-retry" lane on
 * the left (each pause state sits on the SAME row as the step it's triggered
 * from), the happy-path spine in the middle, and the terminal failure sinks on
 * the right (each on its source step's row). Coordinates are the top-left corner
 * of each node box.
 */

export const NODE_W = 220;
export const NODE_H = 64;
/** Blocked-lane and sink nodes are the same width, just a little shorter. */
export const NODE_W_SM = 220;
export const NODE_H_SM = 56;

// Column x-origins and the shared row grid.
const COL_LANE = 28;
const COL_SPINE = 398;
const COL_SINK = 708;
const TOP = 24;
const ROW_H = 116;

// Extra room before the two capped steps so a concurrency-gate node can sit
// inline with an arrow in and an arrow out: +1 gap before Researching (row 2)
// and +1 before Searching the web (row 3).
const GATE_GAP = 64;
/** Top-left y of a full-height spine node on row `r`. */
const spineY = (r: number) => TOP + r * ROW_H + (r >= 2 ? GATE_GAP : 0) + (r >= 3 ? GATE_GAP : 0);
/** Top-left y of a short (h=NODE_H_SM) node centered on spine row `r`. */
const alignY = (r: number) => spineY(r) + (NODE_H - NODE_H_SM) / 2;
/** Center y of the gate node that sits in the gap between rows `from` and `to`. */
const gateMidY = (from: number, to: number) => (spineY(from) + NODE_H + spineY(to)) / 2;

export const CONTENT_W = COL_SINK + NODE_W + 24;
export const CONTENT_H = spineY(8) + NODE_H + 24;

export type FlowNodeKind =
  | 'entry' // synthetic: how a person gets in
  | 'step' // an in-flight processing status
  | 'gate' // synthetic: the filter decision
  | 'blocked' // alive, auto-retrying
  | 'review' // human gate
  | 'sinkFail' // terminal dead-end
  | 'sinkDone'; // terminal success (enrolled)

/** Which per-template branch an element belongs to, for emphasis / dimming. */
export type FlowBranch =
  | 'personalized' // only reached when the template has personalized steps
  | 'noPersonalization' // the bypass taken when it doesn't
  | 'filter'; // only meaningful when the template has filter rules

export interface FlowNode {
  id: string;
  /** When set, the node maps to a candidate status and shows a live count. */
  status?: CandidateStatusParam;
  title: string;
  subtitle: string;
  kind: FlowNodeKind;
  branch?: FlowBranch;
  x: number;
  y: number;
  w?: number;
  h?: number;
}

export interface FlowEdge {
  from: string;
  to: string;
  label?: string;
  /** This transition is driven by a person (not the worker) — rendered violet. */
  userAction?: boolean;
  branch?: FlowBranch;
  /** Anchor side on the source node. Defaults to 'down'. */
  fromSide?: SvrEdgeNormal;
  /** Anchor side on the target node. Defaults to 'up'. */
  toSide?: SvrEdgeNormal;
}

export const FLOW_NODES: FlowNode[] = [
  // ---- happy-path spine (top → bottom), one per row ----
  { id: 'entry', kind: 'entry', title: 'Person added', subtitle: 'Bulk add · Sales Nav · webhook', x: COL_SPINE, y: spineY(0) },
  { id: 'pending', status: 'pending', kind: 'step', title: 'Pending', subtitle: 'Queued, not yet picked up', x: COL_SPINE, y: spineY(1) },
  { id: 'researching', status: 'researching', kind: 'step', title: 'Researching', subtitle: 'Fetching the profile', x: COL_SPINE, y: spineY(2) },
  { id: 'gathering_sources', status: 'gathering_sources', kind: 'step', title: 'Searching the web', subtitle: 'Gathering sources (Tavily)', x: COL_SPINE, y: spineY(3) },
  { id: 'synthesizing', status: 'synthesizing', kind: 'step', title: 'Synthesizing dossier', subtitle: 'Building the dossier (AI)', x: COL_SPINE, y: spineY(4) },
  { id: 'filter', kind: 'gate', title: 'Filter check', subtitle: 'Do they qualify?', x: COL_SPINE, y: spineY(5) },
  { id: 'personalizing', status: 'personalizing', kind: 'step', branch: 'personalized', title: 'Drafting', subtitle: 'Personalized drafts (AI)', x: COL_SPINE, y: spineY(6) },
  { id: 'pending_review', status: 'pending_review', kind: 'review', branch: 'personalized', title: 'Needs review', subtitle: 'Awaiting human review', x: COL_SPINE, y: spineY(7) },
  { id: 'promoted', status: 'promoted', kind: 'sinkDone', title: 'Enrolled', subtitle: 'Active in the campaign', x: COL_SPINE, y: spineY(8) },

  // ---- terminal failure sinks (right column, on their source step's row) ----
  { id: 'removed', status: 'removed', kind: 'sinkFail', title: 'Removed', subtitle: 'Backed out by the user', x: COL_SINK, y: alignY(1), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'research_failed', status: 'research_failed', kind: 'sinkFail', title: 'Research error', subtitle: "Couldn't reach the profile", x: COL_SINK, y: alignY(2), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'synthesize_failed', status: 'synthesize_failed', kind: 'sinkFail', title: 'Dossier error', subtitle: 'Synthesis errored out', x: COL_SINK, y: alignY(4), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'filter_failed', status: 'filter_failed', kind: 'sinkFail', branch: 'filter', title: 'Filtered out', subtitle: "Didn't match the rules", x: COL_SINK, y: alignY(5), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'personalize_failed', status: 'personalize_failed', kind: 'sinkFail', branch: 'personalized', title: 'Drafting error', subtitle: 'Every draft errored', x: COL_SINK, y: alignY(6), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'rejected', status: 'rejected', kind: 'sinkFail', branch: 'personalized', title: 'Rejected', subtitle: 'Rejected or already enrolled', x: COL_SINK, y: alignY(7), w: NODE_W_SM, h: NODE_H_SM },

  // ---- blocked / auto-retry pause states (left column, on the row of the step
  //      they're triggered from — see BLOCKED_SOURCE in the renderer) ----
  // Level with the Tavily gate (row 2→3) — it's that gate (web search) being full.
  { id: 'awaiting_capacity', status: 'awaiting_capacity', kind: 'blocked', title: 'Waiting for capacity', subtitle: 'Auto-resumes', x: COL_LANE, y: gateMidY(2, 3) - NODE_H_SM / 2, w: NODE_W_SM, h: NODE_H_SM },
  { id: 'account_disconnected', status: 'account_disconnected', kind: 'blocked', title: 'LinkedIn disconnected', subtitle: 'Resumes on reconnect', x: COL_LANE, y: alignY(2), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'rate_limited', status: 'rate_limited', kind: 'blocked', title: 'Rate limited', subtitle: 'Auto-resumes', x: COL_LANE, y: alignY(3), w: NODE_W_SM, h: NODE_H_SM },
  { id: 'retrying', status: 'retrying', kind: 'blocked', title: 'Retrying', subtitle: 'Backing off, will retry', x: COL_LANE, y: alignY(4), w: NODE_W_SM, h: NODE_H_SM },
];

export const FLOW_EDGES: FlowEdge[] = [
  // spine
  { from: 'entry', to: 'pending', label: 'added', userAction: true },
  // pending → researching and researching → gathering_sources flow THROUGH the
  // concurrency-gate nodes (arrows drawn in CandidateFlowGraph.tsx), so there's
  // no direct edge here.
  { from: 'gathering_sources', to: 'synthesizing', label: 'sources gathered' },
  { from: 'synthesizing', to: 'filter', label: 'dossier built' },
  { from: 'filter', to: 'personalizing', label: 'passes · personalized', branch: 'personalized' },
  { from: 'personalizing', to: 'pending_review', label: 'drafts ready', branch: 'personalized' },
  { from: 'pending_review', to: 'promoted', label: 'approved', branch: 'personalized', userAction: true },

  // bypass: no personalized steps → straight to enrolled
  {
    from: 'filter',
    to: 'promoted',
    label: 'passes · no personalization',
    branch: 'noPersonalization',
    fromSide: 'left',
    toSide: 'left',
  },

  // terminal failure sinks (horizontal — source & sink share a row)
  { from: 'pending', to: 'removed', label: 'removed', userAction: true, fromSide: 'right', toSide: 'left' },
  { from: 'researching', to: 'research_failed', label: "can't reach", fromSide: 'right', toSide: 'left' },
  { from: 'synthesizing', to: 'synthesize_failed', label: 'errored', fromSide: 'right', toSide: 'left' },
  { from: 'filter', to: 'filter_failed', label: 'filtered out', branch: 'filter', fromSide: 'right', toSide: 'left' },
  {
    from: 'personalizing',
    to: 'personalize_failed',
    label: 'draft failed',
    branch: 'personalized',
    fromSide: 'right',
    toSide: 'left',
  },
  {
    from: 'pending_review',
    to: 'rejected',
    label: 'rejected',
    branch: 'personalized',
    userAction: true,
    fromSide: 'right',
    toSide: 'left',
  },

  // NOTE: the blocked pause states each sit on their source step's row and are
  // wired with a trigger arrow (step → pause) + a resume arrow (pause → step)
  // drawn directly in CandidateFlowGraph.tsx (see BLOCKED_SOURCE / BLOCKED_TRIGGER).
];
